/**
 * Межі записів у редакторі ролі: «тільки власні» / «власні і на їх підставі» / «всі».
 *
 * ч.1312: межа задається по ВИДУ записів (реалізації, рахунки, контрагенти…), а не по
 * окремому праву. ч.1317 (рішення власника «упростить визуально»): замість окремого блоку
 * «Чиї записи бачить користувач» — рядок з перемикачем ПІД відміченим правом виду. Вид має
 * кілька прав (створення / проведення / видалення реалізацій) — рядок стоїть під першим
 * відміченим правом виду, щоб той самий перемикач не дублювався. Відмітили галку права, вид
 * якого ще не був у ролі, — за замовчуванням «тільки власні» (рішення власника); права,
 * підставлені з готової ролі-основи, лишаються «всі».
 * Перелік видів — з сервера (`GET /access-roles/record-scope-kinds`, record_scope.py).
 */
import { RECORD_SCOPE_LABELS, type RecordScope, type RecordScopeKind } from '@/api/admin';

/** Види, до яких належить право (за префіксами прав виду). */
export function kindsOfPermission(kinds: RecordScopeKind[] | undefined, code: string): RecordScopeKind[] {
  return (kinds ?? []).filter((k) => k.permission_prefixes.some((p) => code.startsWith(p)));
}

/** Види, з якими працює роль з цими правами (як `kinds_for_permissions` на сервері). */
export function relevantKinds(kinds: RecordScopeKind[] | undefined, permissions: Set<string>): RecordScopeKind[] {
  const codes = Array.from(permissions);
  return (kinds ?? []).filter((k) => codes.some((c) => k.permission_prefixes.some((p) => c.startsWith(p))));
}

/** Межі лише для видів, з якими роль працює: межа виду без прав нічого б не означала. */
export function scopesToSave(scopes: Record<string, RecordScope>, kinds: RecordScopeKind[]): Record<string, RecordScope> {
  const allowed = new Set(kinds.map((k) => k.code));
  return Object.fromEntries(Object.entries(scopes).filter(([k]) => allowed.has(k)));
}

/** Після відмітки галок: вид, якого в ролі ще не було, отримує «тільки власні». */
export function withDefaultOwn(
  scopes: Record<string, RecordScope>,
  before: Set<string>,
  after: Set<string>,
  kinds: RecordScopeKind[] | undefined,
): Record<string, RecordScope> {
  const had = new Set(relevantKinds(kinds, before).map((k) => k.code));
  const next = { ...scopes };
  for (const k of relevantKinds(kinds, after)) {
    if (!had.has(k.code)) next[k.code] = 'OWN';
  }
  return next;
}

/** Під яким правом показати перемикач виду: перше відмічене право виду в порядку показу. */
export function scopeRowOwners(kinds: RecordScopeKind[] | undefined, orderedChecked: string[]): Map<string, string> {
  const owners = new Map<string, string>();
  for (const code of orderedChecked) {
    for (const k of kindsOfPermission(kinds, code)) {
      if (!owners.has(k.code)) owners.set(k.code, code);
    }
  }
  return owners;
}

const TENANT_LEVEL_TIP = 'Контрагенти спільні для всіх організацій акаунта: «тільки власні» діє, '
  + 'якщо так стоїть у кожній ролі людини';

/** Рядок під правом: «тільки власні · власні і на їх підставі · всі». */
export function PermScopeRow({ kind, value, onChange, showLabel, rowKey }: {
  kind: RecordScopeKind;
  value: RecordScope | 'ALL';
  onChange: (v: RecordScope | 'ALL') => void;
  /** Під одним правом кілька видів (ПКО і авансові звіти) — підписати, про який вид мова. */
  showLabel: boolean;
  rowKey: string;
}) {
  const opts: (RecordScope | 'ALL')[] = kind.chain ? ['OWN', 'OWN_CHAIN', 'ALL'] : ['OWN', 'ALL'];
  return (
    <div data-testid={`perm-scope-${kind.code}`} role="radiogroup"
      aria-label={`Чиї записи: ${kind.label}`}
      title={kind.tenant_level ? TENANT_LEVEL_TIP : `Чиї записи виду «${kind.label}» бачить і змінює користувач`}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.15rem 0.7rem',
        margin: '0 0 0.25rem 1.55rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
      {showLabel && <span style={{ fontWeight: 600 }}>{kind.label}:</span>}
      {opts.map((o) => (
        <label key={o} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer',
          color: value === o ? 'var(--text)' : undefined, fontWeight: value === o ? 600 : 400 }}>
          <input type="radio" name={`scope-${rowKey}-${kind.code}`} value={o} checked={value === o}
            onChange={() => onChange(o)} style={{ width: 'auto', margin: 0 }} />
          {RECORD_SCOPE_LABELS[o]}
        </label>
      ))}
    </div>
  );
}
